import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import dayjs from 'dayjs';

/**
 * 入力された数値を3桁のカンマ区切りに変換
 * @param {number} data (ex. 123456789.1234)
 * @return {string} カンマ区切りされた値 (ex. 123,456,789.1234)
 */
export const numInsertComma = (data: number) => {
  return Intl.NumberFormat().format(data) ?? 0;
};

// 入力された値が上限値と下限値のを超えていた場合に上限値もしくは下限値に変換
export const numBetweenMinMax = (data: number, min: number, max: number) => {
  return data > max ? max : data < min ? min : data;
};

// DateSelectorが返すUTC値も、画面に表示されたローカル日付の一日全体として扱う。
export const setApiDateFormat = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
  return [
    formatUtcFetchTimestamp(start.local().startOf('day')),
    formatUtcFetchTimestamp(end.local().endOf('day')),
  ];
};

export const setTableDateFormat = (date: string) => {
  return dayjs(date).format('YYYY/MM/DD');
};

/**
 * 任意の桁で四捨五入する関数
 * @param {number} data 四捨五入する数値
 * @param {number} decimal どの桁で四捨五入するか（0で整数, 1で小数点第一 ...）
 * @return {number} 四捨五入した値
 */
export const mathRound = (data: number, decimal = 0) => {
  const base = 10 ** decimal;
  return Math.round(data * base) / base;
}

/**
* 任意の桁で切り上げする関数
* @param {number} data 切り上げする数値
* @param {number} decimal どの桁で切り上げするか（0で整数, 1で小数点第一 ...）
* @return {number} 切り上げした値
*/
export const mathCeil = (data: number, decimal = 0) => {
  const base = 10 ** decimal;
  return Math.ceil(data * base) / base;
}

/**
* 任意の桁で切り捨てする関数
* @param {number} data 切り捨てする数値
* @param {number} decimal どの桁で切り捨てするか（0で整数, 1で小数点第一 ...）
* @return {number} 切り捨てした値
*/
export const mathFloor = (data: number, decimal = 0) => {
  const base = 10 ** decimal;
  return Math.floor(data * base) / base;
}

/**
 * 数値を区切りの良い値に切り上げる関数。
 * 値を 1, 2, 5, 10 × 10^n の形式に丸める。
 * （ex. 37 → 50, 842 → 1000, 0.91 → 1）
 *
 * @param {number} value 切り上げ対象の数値
 * @return {number} 値を切り上げた結果
 */
export const calcAxisMax = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 10;

  const exp = Math.floor(Math.log10(value));
  const f = value / 10 ** exp;

  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * 10 ** exp;
};
